import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { interviewAPI, applicationAPI, sessionAPI } from '../../services/api';
import { FaArrowLeft, FaCalendarAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Button from '../../components/Button';

const ScheduleInterview = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [applications, setApplications] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [formData, setFormData] = useState({
        application_id: '',
        interview_date: '',
        interview_time: '',
        interview_type: 'virtual', // virtual, in_person
        location: '',
        meeting_link: '',
        notes: ''
    });

    useEffect(() => {
        loadSessions();
    }, []);

    useEffect(() => {
        if (selectedSession) {
            loadApplications();
        }
    }, [selectedSession]);

    const loadSessions = async () => {
        try {
            const res = await sessionAPI.getAll({ status: 'active' });
            const newSessions = res.sessions || [];
            // CRITICAL: Only update state if data actually changed to prevent infinite loops
            setSessions(prev => {
                const prevStr = JSON.stringify(prev);
                const newStr = JSON.stringify(newSessions);
                return prevStr === newStr ? prev : newSessions;
            });
            if (newSessions.length > 0) {
                setSelectedSession(prev => {
                    const firstId = newSessions[0].id;
                    return prev === firstId ? prev : firstId;
                });
            }
        } catch (err) {
            console.error('Failed to load sessions:', err);
        }
    };

    const loadApplications = async () => {
        if (!selectedSession) return;
        try {
            const res = await applicationAPI.getBySession(selectedSession, { 
                status: 'submitted' 
            });
            setApplications(res.applications || []);
        } catch (err) {
            console.error('Failed to load applications:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await interviewAPI.schedule({
                ...formData,
                interview_datetime: `${formData.interview_date}T${formData.interview_time}`
            });
            toast.success('Interview scheduled successfully!');
            navigate('/dashboard');
        } catch (err) {
            console.error('Failed to schedule interview:', err);
            toast.error(err.message || 'Failed to schedule interview. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-4xl mx-auto px-6 py-8">
                <Link to="/dashboard" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
                    <FaArrowLeft className="mr-2" /> Back to Dashboard
                </Link>

                <div className="bg-white rounded-xl shadow-lg p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <FaCalendarAlt className="text-3xl text-primary-600" />
                        <h1 className="text-3xl font-bold text-gray-900">Schedule Interview</h1>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Session
                            </label>
                            <select
                                value={selectedSession || ''}
                                onChange={(e) => setSelectedSession(parseInt(e.target.value))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                            >
                                {sessions.map(session => (
                                    <option key={session.id} value={session.id}>{session.name}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Applicant *
                            </label>
                            <select
                                required
                                value={formData.application_id}
                                onChange={(e) => setFormData({...formData, application_id: e.target.value})}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                            >
                                <option value="">Select an applicant</option>
                                {applications.map(app => (
                                    <option key={app.id} value={app.id}>
                                        {app.first_name} {app.surname} - {app.email}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Interview Date *
                                </label>
                                <input
                                    type="date"
                                    required
                                    value={formData.interview_date}
                                    onChange={(e) => setFormData({...formData, interview_date: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Interview Time *
                                </label>
                                <input
                                    type="time"
                                    required
                                    value={formData.interview_time}
                                    onChange={(e) => setFormData({...formData, interview_time: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Interview Type *
                            </label>
                            <select
                                required
                                value={formData.interview_type}
                                onChange={(e) => setFormData({...formData, interview_type: e.target.value})}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                            >
                                <option value="virtual">Virtual</option>
                                <option value="in_person">In Person</option>
                            </select>
                        </div>

                        {formData.interview_type === 'virtual' ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Meeting Link *
                                </label>
                                <input
                                    type="url"
                                    required={formData.interview_type === 'virtual'}
                                    value={formData.meeting_link}
                                    onChange={(e) => setFormData({...formData, meeting_link: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                                    placeholder="https://meet.google.com/..."
                                />
                            </div>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Location *
                                </label>
                                <input
                                    type="text"
                                    required={formData.interview_type === 'in_person'}
                                    value={formData.location}
                                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                                    placeholder="Interview location"
                                />
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Notes (Optional)
                            </label>
                            <textarea
                                rows={4}
                                value={formData.notes}
                                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                                placeholder="Additional notes for the interview..."
                            />
                        </div>

                        <div className="flex gap-4 pt-4">
                            <Button type="submit" variant="primary-gradient" className="flex-1 px-6 py-3">
                                    {loading ? 'Scheduling...' : 'Schedule Interview'}
                            </Button>
                            <button
                                type="button"
                                onClick={() => navigate('/dashboard')}
                                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ScheduleInterview;

